// The Khipur long knot: a cord with turns wound round it, like the long knots a
// khipu used for the ones digit (the number of turns was the digit).
//
// Two numbers describe a knot at any moment:
//   p: how many turns are wound on (0..n, fractional while one is winding)
//   c: how tight it is (0 loose and spread out, 1 cinched; a pull overshoots)
//
// Every turn is the same shape shifted along the cord, so each neighbouring
// pair sits exactly alike: tight turns keep a clear gap and never bite into
// each other. Coordinates are in knot units: the cord is 6 wide.

export type KnotState = { p: number; c: number };

/** A turn as an SVG path, its cubic's points, and how much is wound (0..1). */
export type KnotTurn = { d: string; m: number[]; f: number };

export type KnotGeometry = {
  cord: string;
  turns: KnotTurn[];
  /** Cord width. */
  w: number;
  /** Turn width. */
  tw: number;
  /** Turn width plus the gap cut into what it crosses. */
  cw: number;
};

export const KNOT = {
  n: 3,
  w: 6,
  tw: 6.4,
  h: 10.2,
  pitchTight: 8.1,
  pitchLoose: 11,
  slantTight: 2.3,
  slantLoose: 3.6,
  bow: 0.9,
  gap: 1.2,
};

export type KnotOptions = Partial<typeof KNOT> & {
  p?: number;
  c?: number;
  cx?: number;
  cy?: number;
  x0?: number;
  x1?: number;
  /** Slack: how far the cord dips below its ends at its middle. */
  sag?: number;
};

const r2 = (v: number) => Math.round(v * 100) / 100;

export const knotGeometry = (o: KnotOptions = {}): KnotGeometry => {
  const g = { ...KNOT, ...o };
  const { n, w, tw, h, bow, gap } = g;
  const cx = o.cx ?? 32;
  const cy = o.cy ?? 32;
  const c = o.c ?? 1;
  const p = Math.max(0, Math.min(n, o.p ?? n));
  const pitch = g.pitchLoose + (g.pitchTight - g.pitchLoose) * c;
  const slant = g.slantLoose + (g.slantTight - g.slantLoose) * c;
  const first = cx - ((n - 1) * pitch) / 2;
  // A slack cord is a curve through its ends, `sag` low at its middle; each
  // turn sits on it.
  const x0 = o.x0 ?? 4;
  const x1 = o.x1 ?? 60;
  const sag = o.sag ?? 0;
  const dip = (x: number) => {
    const t = (x - x0) / (x1 - x0);
    return 4 * sag * t * (1 - t);
  };
  const turns: KnotTurn[] = [];
  for (let i = 0; i < n; i++) {
    const x = first + i * pitch;
    const y = cy + dip(x);
    // Control points sit on the straight line between the ends, pushed along
    // the cord by `bow`: each turn is a band with a gentle curve, not an S.
    const m = [
      x - slant,
      y - h,
      x - slant / 3 + bow,
      y - h / 3,
      x + slant / 3 + bow,
      y + h / 3,
      x + slant,
      y + h,
    ].map(r2);
    const d = `M${m[0]} ${m[1]}C${m[2]} ${m[3]} ${m[4]} ${m[5]} ${m[6]} ${m[7]}`;
    turns.push({ d, m, f: Math.max(0, Math.min(1, p - i)) });
  }
  return {
    cord: sag
      ? `M${r2(x0)} ${r2(cy)}Q${r2((x0 + x1) / 2)} ${r2(cy + 2 * sag)} ${r2(x1)} ${r2(cy)}`
      : `M${r2(x0)} ${r2(cy)}H${r2(x1)}`,
    turns,
    w,
    tw,
    cw: tw + 2 * gap,
  };
};

export type Ease = "lin" | "inOut" | "out" | "cinch";

export const EASE: Record<Ease, (t: number) => number> = {
  lin: (t) => t,
  inOut: (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2),
  out: (t) => 1 - Math.pow(1 - t, 3),
  // Pulled tight: quick, overshoots past tight and settles.
  cinch: (t) => 1 + 3.1 * Math.pow(t - 1, 3) + 2.1 * Math.pow(t - 1, 2),
};

/** One leg of a knot's motion: [ms, from, to, easing]. */
export type Leg = [number, KnotState, KnotState, Ease];
export type Cycle = Leg[];

export const S = (p: number, c: number): KnotState => ({ p, c });

export const cycleLength = (cycle: Cycle): number =>
  cycle.reduce((sum, leg) => sum + leg[0], 0);

/** Where a knot is `ms` into `cycle`; past the end it rests at the last state. */
export const stateAt = (ms: number, cycle: Cycle): KnotState => {
  let t = ms;
  for (const [d, a, b, e] of cycle) {
    if (t <= d) {
      const k = EASE[e](d ? t / d : 1);
      return { p: a.p + (b.p - a.p) * k, c: a.c + (b.c - a.c) * k };
    }
    t -= d;
  }
  return cycle[cycle.length - 1][2];
};

/** Wind n turns on loosely, then pull them tight. */
export const tieCycle = (n: number, from: KnotState = S(0, 0)): Cycle => [
  [120, from, S(0, 0), "inOut"],
  [180 * n + 100, S(0, 0), S(n, 0), "lin"],
  [420, S(n, 0), S(n, 1), "cinch"],
];

/** Loosen, then unwind every turn. */
export const untieCycle = (n: number, from: KnotState = S(n, 1)): Cycle => [
  [300, from, S(from.p, 0), "inOut"],
  [160 * from.p + 120, S(from.p, 0), S(0, 0), "lin"],
];

/** A tug: give a little, then snap tight again. */
export const tugCycle = (n: number, from: KnotState = S(n, 1)): Cycle => [
  [200, from, S(n, 0.35), "out"],
  [480, S(n, 0.35), S(n, 1), "cinch"],
];

export const prefersReducedMotion = (): boolean =>
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
